import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { IndexHeader } from "./IndexHeader";
import { updateColors, useTheme } from "../hooks/ThemeContext";
import "../styles/ProfilePage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { ProfileCollegeSelect } from "../objects/ProfileCollegeSelect";

const UserIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
    </svg>
);

const CrownIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563.563 0 00.475-.345L11.48 3.5z"
        />
    </svg>
);

const SkillsIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
        />
    </svg>
);

const FileIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        style={{ width: 20, height: 20 }}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
    </svg>
);

function Profile() {
    const [currUser, setCurrentUser] = useState({});
    const [isLoaded, setIsLoaded] = useState(false);
    const [isProfileLoaded, setIsProfileLoaded] = useState(false);

    const [isBillingLoaded, setIsBillingLoaded] = useState(true);
    const [logoutLoad, setLogoutLoad] = useState(false);
    const [deleteLoad, setDeleteLoad] = useState(false);

    const { theme, setTheme } = useTheme();

    const theForm = useRef();
    const navigate = useNavigate();

    const location = useLocation();

    const [oauthError, setOauthError] = useState(false);


    const [reportedCollege, setReportedCollege] = useState("");

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch(`/api/index`);
                if (!response.ok) throw new Error(`/api/index failed with status ${response.status}`);
                const data = await response.json();
                if (data.colors) {
                    localStorage.setItem("theme-colors", JSON.stringify(data.colors));
                    updateColors(data.colors, setTheme);
                }
                if (data.user) {
                    setCurrentUser(data.user);
                    setReportedCollege(data.user?.reported_college || "");
                } else {
                    setCurrentUser({ na: true });
                    navigate("/login");
                }
                setIsLoaded(true);
                setIsProfileLoaded(true);
            } catch (err) {
                Sentry.captureException(err);
                setCurrentUser({ error: true });
                setIsLoaded(true);
                setIsProfileLoaded(true);
            }
        };
        load();
    }, [navigate, setTheme]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const hasOauthError = params.get("oauthError") === "true";

        if (hasOauthError) {
            setOauthError(true);

            // Remove the param so it doesn't keep showing on refresh
            params.delete("oauthError");
            navigate(
                {
                    pathname: location.pathname,
                    search: params.toString() ? `?${params.toString()}` : "",
                },
                { replace: true }
            );
        }
    }, [location.pathname, location.search, navigate]);


    const UpdateProfile = async () => {
        const emailDomain =
            (currUser?.email || "").toLowerCase().split("@")[1] || "";

        const collegeLocked = emailDomain.endsWith(".edu");

        if (!collegeLocked && !reportedCollege?.trim()) {
            alert("Please select a valid college from the list.");
            return;
        }

        setIsProfileLoaded(false);
        try {
            const result = await fetch("/api/update_profile", {
                method: "POST",
                body: new FormData(theForm.current),
            });
            const data = await result.json();

            if (!result.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                alert(data.error_message || 'Error updating profile. Please try again.');
                return;
            }

            if (data.user) {
                setCurrentUser(data.user);
            }
            if (data.error_message) {
                alert(data.error_message);
            }
        } catch (err) {
            Sentry.captureException(err);
            alert('Error updating profile. Please try again.');
        } finally {
            setIsProfileLoaded(true);
        }
    };

    const HandleBilling = async () => {
        setIsBillingLoaded(false);
        try {
            const result = await fetch("/api/get_billing", { method: "POST" });
            const data = await result.json();

            if (!result.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                alert(data.error_message || 'Error loading billing. Please try again.');
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
            }
            if (data.error_message) {
                alert(data.error_message);
            }
        } catch (err) {
            Sentry.captureException(err);
            alert('Error loading billing. Please try again.');
        } finally {
            setIsBillingLoaded(true);
        }
    };

    const OnLogout = async () => {
        setLogoutLoad(true);
        try {
            const response = await fetch("/api/logout", { method: "POST" });
            const data = await response.json();

            if (!response.ok) {
                alert(data.error_message || 'Error logging out. Please try again.');
                return;
            }

            window.location.href = "/";
        } catch (err) {
            Sentry.captureException(err);
            alert('Error logging out. Please try again.');
        } finally {
            setLogoutLoad(false);
        }
    };

    const OnDeleteAccount = async () => {
        const confirmed = window.confirm("Are you sure you want to delete your account?");
        if (!confirmed) return;

        setDeleteLoad(true);

        try {
            const response = await fetch("/api/delete_account", { method: "POST" });
            const data = await response.json();

            if (!response.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                if (data.error_code === 'active_subscription') {
                    alert('You must cancel your subscription before deleting account.');
                } else {
                    alert(data.error_message || 'Error deleting account. Please try again.');
                }
                return;
            }

            window.location.href = "/";
        } catch (err) {
            Sentry.captureException(err);
            alert('Error deleting account. Please try again.');
        } finally {
            setDeleteLoad(false);
        }
    };

    // Truncate filename if too long
    const formatFilename = (filename) => {
        if (!filename) return "No resume uploaded";
        if (filename.length > 50) {
            return filename.substring(0, 47) + "...";
        }
        return filename;
    };

    const authType = (currUser?.auth_type || "").toLowerCase();
    const googleManaged = authType === "google";
    const microsoftManaged = authType === "microsoft";
    const oauthManaged = googleManaged || microsoftManaged;
    const emailLower = (currUser?.email || "").toLowerCase();
    const isGoogleEmail = emailLower.endsWith("@gmail.com") || emailLower.endsWith("@googlemail.com");
    const isPasswordManaged = !oauthManaged && authType === "password";
    const showConnectGoogle = isPasswordManaged && isGoogleEmail;

    const startGoogle = () => {
        const backend = window.location.origin;

        // next should be a PATH like "/register_complete"
        const nextParam = encodeURIComponent("/results");
        const cameFromParam = encodeURIComponent("profile");

        window.location.href = `${backend}/auth/google/start?next=${nextParam}&cameFrom=${cameFromParam}`;
    };

    const canManageBilling =
        currUser?.plan === "premium" && !!currUser?.subscription_status;

    const billingDisabledReason =
        currUser?.plan !== "premium"
            ? "Upgrade to premium to manage billing."
            : !currUser?.subscription_status
                ? "Your premium access is provided by a partner. Billing is not managed here."
                : "Manage your billing and subscription settings.";

    return (
        <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <>
            <header>
                <div className="top-header" id="top-header">
                    <IndexHeader
                        user={currUser}
                        firstWait={isLoaded}
                        upgradeRedirect={"profile"}
                    />
                </div>
            </header>

            {isLoaded ? (
                <div className="content">
                    <h1 className="page-title">Your Profile</h1>
                    <p className="page-subtitle">
                        Manage your account settings and resume
                    </p>

                    {/* Resume File Row - Compact */}
                    <div className="resume-file-row">
                        <div className="resume-file-info">
                            <FileIcon />
                            <span className="resume-filename">
                                {formatFilename(currUser?.resume_file)}
                            </span>
                        </div>
                        {currUser.plan === "premium" ? (
                            <button
                                onClick={() => navigate("/index?noredir=1")}
                                className="btn btn-secondary btn-sm"
                            >
                                Change Resume
                            </button>
                        ) : (
                            <label className="btn btn-disabled btn-sm">
                                Change Resume
                                <div className="tooltip">
                                    Upgrade to premium to upload a new resume
                                </div>
                            </label>
                        )}
                    </div>

                    <div className="profile-grid">
                        {/* Skills Card */}
                        <div className="profile-card skills-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <SkillsIcon />
                                </div>
                                <h2 className="card-title">Skills</h2>
                            </div>

                            <div className="skills-container">
                                {currUser?.skills && currUser.skills.length > 0 ? (
                                    <div className="skills-tags">
                                        {currUser.skills.map((skill, index) => (
                                            <span key={index} className="skill-tag">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="empty-state">No skills found in your resume</p>
                                )}
                            </div>

                            {currUser?.intern_titles && currUser.intern_titles.length > 0 && (
                                <>
                                    <div className="section-divider" />
                                    <h3 className="subsection-title">
                                        Job Titles You're Searching For
                                    </h3>
                                    <div className="job-titles-tags">
                                        {currUser.intern_titles.map((title, index) => (
                                            <span key={index} className="job-title-tag">
                                                {title}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Account Info Card */}
                        <div className="profile-card info-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <UserIcon />
                                </div>
                                <h2 className="card-title">Account Information</h2>
                            </div>

                            {isProfileLoaded ? (
                                <form
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        UpdateProfile();
                                    }}
                                    ref={theForm}
                                    className="profile-form"
                                >
                                    <div className="form-group">
                                        <label className="form-label">Email</label>

                                        {/* Locked email + tooltip */}
                                        <div className="locked-input-wrapper">
                                            <input
                                                type="email"
                                                name="email"
                                                aria-label="Email address"
                                                value={currUser?.email || ""}
                                                disabled
                                                placeholder="Email"
                                                className="form-input"
                                            />
                                            <div className="locked-tooltip">
                                                You cannot change the email associated with your account.
                                                If you have an issue, please contact support at{" "}
                                                <span className="locked-tooltip-email">support@rezify.ai</span>
                                            </div>
                                        </div>

                                        {/* Managed by Google/Microsoft OR show change password link */}
                                        {oauthManaged ? (
                                            <div className="managed-by-row">
                                                <span className="managed-by-text">
                                                    Managed by {googleManaged ? "Google" : "Microsoft"}
                                                </span>
                                                <img
                                                    src={googleManaged ? "/static/google-logo.png" : "/static/microsoft-logo.png"}
                                                    alt={googleManaged ? "Google" : "Microsoft"}
                                                    className="managed-by-logo"
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="auth-actions-grid">
                                                    <button
                                                        type="button"
                                                        className="auth-action-btn auth-action-btn-primary"
                                                        onClick={() => navigate("/change_password")}
                                                    >
                                                        Change Password
                                                    </button>

                                                    {showConnectGoogle && (
                                                        <button
                                                            type="button"
                                                            className="auth-action-btn auth-action-btn-google"
                                                            onClick={startGoogle}
                                                        >
                                                            <img
                                                                src="/static/google-logo.png"
                                                                alt="Google"
                                                                className="auth-action-logo"
                                                            />
                                                            Connect Google Account
                                                        </button>
                                                    )}
                                                </div>

                                                {oauthError && (
                                                    <div className="error-box">
                                                        Authentication failed. Please try again.
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">College</label>

                                        <input
                                            type="hidden"
                                            name="college"
                                            value={reportedCollege || ""}
                                        />

                                        <ProfileCollegeSelect
                                            email={currUser?.email || ""}
                                            value={reportedCollege || ""}
                                            setValue={setReportedCollege}
                                        />
                                    </div>


                                    <div className="name-row">
                                        <div className="form-group">
                                            <label className="form-label">First Name</label>
                                            <input
                                                type="text"
                                                name="first_name"
                                                aria-label="First name"
                                                defaultValue={currUser?.first_name || ""}
                                                placeholder="First Name"
                                                required
                                                className="form-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Last Name</label>
                                            <input
                                                type="text"
                                                name="last_name"
                                                aria-label="Last name"
                                                defaultValue={currUser?.last_name || ""}
                                                placeholder="Last Name"
                                                required
                                                className="form-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn btn-primary">
                                            Save Changes
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="spinner" style={{ margin: "40px auto" }} />
                            )}
                        </div>

                        {/* Plan Card */}
                        <div className="profile-card plan-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <CrownIcon />
                                </div>
                                <h2 className="card-title">Subscription</h2>
                            </div>

                            <div className="plan-content">
                                <div className="plan-info">
                                    <span
                                        className={`plan-badge ${
                                            currUser.plan === "premium" ? "premium" : "basic"
                                        }`}
                                    >
                                        {currUser.plan === "premium" ? "Premium" : "Basic"}
                                    </span>
                                    <div>
                                        <p className="plan-description">
                                            {currUser.plan === "premium"
                                                ? "You have full access to all Rezify features"
                                                : "Upgrade to premium for unlimited access"}
                                        </p>
                                        {currUser.plan === "premium" &&
                                            currUser.stripe_meta?.subscription_to_be_cancelled && (
                                                <p className="cancel-notice">
                                                    ⚠️ Cancels on{" "}
                                                    {new Date(
                                                        currUser.stripe_meta.cancel_date * 1000
                                                    ).toDateString()}
                                                </p>
                                            )}
                                        {currUser.plan === "premium" &&
                                            !currUser.subscription_status && (
                                                <div className="partner-info">
                                                    Partnered with{" "}
                                                    <img src={theme.logo} alt="Partner Logo" />
                                                </div>
                                            )}
                                    </div>
                                </div>

                                {currUser.plan === "premium" &&
                                    currUser.subscription_status && (
                                        <button
                                            onClick={(event) => {
                                                event.preventDefault();
                                                HandleBilling();
                                            }}
                                            className="btn btn-secondary"
                                        >
                                            {isBillingLoaded ? (
                                                "Manage Subscription"
                                            ) : (
                                                <div className="spinner" />
                                            )}
                                        </button>
                                    )}
                            </div>
                        </div>

                        {/* Manage Account Card (NEW) */}
                        <div className="profile-card manage-account-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    <UserIcon />
                                </div>
                                <h2 className="card-title">Manage Account</h2>
                            </div>

                            <div className="manage-account-grid">
                                {/* Change password only if NOT google/microsoft managed */}
                                {!oauthManaged ? (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate("/change_password")}
                                    >
                                        Change Password
                                    </button>
                                ) : (
                                    <div className="btn btn-disabled manage-disabled">
                                        Change Password
                                        <div className="tooltip">
                                            Your account is managed by {googleManaged ? "Google" : "Microsoft"}.
                                        </div>
                                    </div>
                                )}

                                {/* Manage billing: always show (enabled/disabled with tooltip) */}
                                {canManageBilling ? (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            HandleBilling();
                                        }}
                                    >
                                        {isBillingLoaded ? "Manage Billing" : <div className="spinner" />}
                                    </button>
                                ) : (
                                    <div className="btn btn-disabled manage-disabled">
                                        Manage Billing
                                        <div className="tooltip">{billingDisabledReason}</div>
                                    </div>
                                )}

                                {/* Logout */}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => OnLogout()}
                                >
                                    {logoutLoad ? <div className="spinner" /> : "Logout"}
                                </button>

                                {/* Delete account */}
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => OnDeleteAccount()}
                                >
                                    {deleteLoad ? <div className="spinner" /> : "Delete Account"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {currUser.resume_file && (
                        <button
                            onClick={() => navigate("/results")}
                            className="btn btn-primary back-to-jobs"
                        >
                            ← Back to Job Listings
                        </button>
                    )}
                </div>
            ) : (
                <div className="spinner" style={{ margin: "100px auto" }} />
            )}
        </>
        </Sentry.ErrorBoundary>
    );
}

export { Profile };
